import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { NumericFormat } from 'react-number-format';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { LookupField } from '../LookupField';
import type { Fetcher } from '../LookupField/services/api.types';
import { LookupReadonlyValue } from './LookupReadonlyValue';
import { readonlyBase, controlHeight } from './fieldStyles';
import type {
  FieldRendererProps,
  ControlType,
  LookupFieldConfig,
  ValidationRule,
} from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';
import { useMasterDocumentFormatters } from './formatters';
import { evaluateFieldValidations, isEmptyValue } from './fieldValidation';

// ─── Estilos base (alineados con Input del UI Kit) ─────────

const inputBase = clsx(
  // Sin padding vertical: la altura de los controles de una línea la fija `controlHeight`
  // (§D.2) y la del `textarea` su propio `min-h`. `py-1.5` aquí daba 34px de control.
  'w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-content-primary',
  'placeholder:text-content-tertiary',
  'hover:border-primary-600',
  'focus:outline-hidden focus:border-primary-600 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'transition-all duration-150',
  'dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary',
  'dark:placeholder:text-dark-content-tertiary',
  'dark:hover:border-dark-border-custom',
  'dark:focus:border-dark-border-custom dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
);

const inputError = 'border-error-border focus:border-error-border focus:ring-error-border/30 dark:border-error-border dark:focus:border-error-border dark:focus:ring-error-border/30';

// Controles "con valor" sobre los que aplica el resaltado de requerido-vacío.
// Se excluyen toggle/checkbox (un booleano nunca está "vacío" de forma útil) y
// los de solo lectura (readonly/display).
const REQUIRED_HIGHLIGHT_CONTROLS: ControlType[] = [
  'text', 'number', 'currency', 'percentage',
  'date', 'select', 'searcher', 'autocomplete', 'textarea',
];


/**
 * Label del canon (ADR-027 §D.1), literal: `text-sm font-bold leading-5` en
 * `content-primary` con `mb-1.5`.
 *
 * Antes era `text-xs font-bold uppercase tracking-wide` en `content-secondary` (#a1a1aa):
 * un segundo estilo de etiqueta dentro del mismo producto. El margen inferior vive aquí
 * (`mb-1.5` = 6px) y no en el `gap` del contenedor, que es como lo hace el canon.
 */
const labelBase = clsx(
  'block text-sm font-bold leading-5 text-content-primary mb-1.5',
  'dark:text-dark-content-primary',
);


// ─── ReadonlyField ─────────────────────────────────────────

function ReadonlyField({ value, isMono }: { value: unknown; isMono?: boolean }) {
  const displayValue = value == null || value === '' ? '—' : String(value);
  return (
    <div className={clsx(readonlyBase, isMono && 'tabular-nums')} title={displayValue}>
      {/* El contenedor pasó a alto fijo (40px, §D.2) para no saltar respecto al control
          editable, así que un valor largo ya no puede crecer hacia abajo: se trunca con
          elipsis y el valor completo queda en el `title`. Antes envolvía y desalineaba la
          rejilla. */}
      <span className="truncate">{displayValue}</span>
    </div>
  );
}

// ─── Controles individuales ────────────────────────────────

function TextInput({
  value,
  onChange,
  placeholder,
  hasError,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
  hasError: boolean;
}) {
  return (
    <input
      type="text"
      className={clsx(inputBase, controlHeight, hasError && inputError)}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

/**
 * Cota numérica declarada en `validations`. Acepta `number` y la cadena que lo
 * represente: los templates escriben `value: 0.0001`, pero el tipo del campo es
 * `unknown` y un consumidor puede haber escrito `'0'`.
 */
function boundOf(validations: ValidationRule[] | undefined, type: 'min' | 'max'): number | undefined {
  const raw = validations?.find((v) => v.type === type)?.value;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/**
 * Importe editable: se ve FORMATEADO mientras se teclea —signo, separador de miles y
 * decimales— y al formulario sigue llegando el número crudo.
 *
 * Va con `react-number-format` y no a mano porque lo difícil aquí no es insertar los
 * puntos: es dónde queda el cursor después de insertarlos. Reformatear en cada
 * pulsación desplaza el texto, y un `setSelectionRange` ingenuo manda el cursor al
 * final en cuanto el usuario edita por el medio o borra un separador. Esa librería
 * resuelve exactamente ese caso; el resto de su superficie no se usa.
 *
 * El separador sale del locale activo, no de constantes: en `es-CO` es `.` para miles
 * y `,` para decimales, y en `en-US` al revés.
 */
function CurrencyInput({
  value,
  onChange,
  placeholder,
  hasError,
  prefijo,
  sufijo,
  decimales,
  min,
  max,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
  hasError: boolean;
  prefijo?: string;
  sufijo?: string;
  decimales: number;
  min?: number;
  max?: number;
}) {
  const { locale } = useMasterDocumentFormatters();
  const separadores = useMemo(() => {
    const partes = new Intl.NumberFormat(locale).formatToParts(12345.6);
    return {
      miles: partes.find((p) => p.type === 'group')?.value ?? ',',
      decimal: partes.find((p) => p.type === 'decimal')?.value ?? '.',
    };
  }, [locale]);

  return (
    <NumericFormat
      value={value == null || value === '' ? '' : Number(value)}
      onValueChange={(v) => onChange(v.floatValue ?? null)}
      thousandSeparator={separadores.miles}
      decimalSeparator={separadores.decimal}
      decimalScale={decimales}
      // Los decimales se completan al SALIR, no mientras se teclea: forzarlos en cada
      // pulsación impide escribir «1.5» —al teclear el separador ya habría dos ceros
      // detrás y el cursor delante de ellos.
      fixedDecimalScale={false}
      allowNegative={min === undefined || min < 0}
      prefix={prefijo ? `${prefijo} ` : undefined}
      suffix={sufijo ? ` ${sufijo}` : undefined}
      placeholder={placeholder}
      inputMode="decimal"
      className={clsx(inputBase, controlHeight, hasError && inputError, 'tabular-nums')}
      onBlur={() => {
        // Mismo clamp que el numérico normal: el `min`/`max` del formulario no lo
        // aplica el control, así que teclear por debajo de la cota entraría igual.
        if (value == null || value === '') return;
        const n = Number(value);
        if (!Number.isFinite(n)) return;
        if (min !== undefined && n < min) onChange(min);
        else if (max !== undefined && n > max) onChange(max);
      }}
    />
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
  hasError,
  prefix,
  suffix,
  min,
  max,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
  hasError: boolean;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
}) {
  const acotado = min !== undefined || max !== undefined;
  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-content-secondary dark:text-dark-content-secondary pointer-events-none select-none">
          {prefix}
        </span>
      )}
      <input
        type="number"
        className={clsx(
          inputBase,
          controlHeight,
          hasError && inputError,
          'tabular-nums',
          prefix && 'pl-7',
          suffix && 'pr-7',
        )}
        min={min}
        max={max}
        /*
         * `step="any"` es OBLIGATORIO en cuanto hay cota, y no es cosmético. Sin `step`
         * el valor por defecto es 1 y el navegador toma `min` como base del escalón: con
         * `min=0.0001` los únicos valores válidos pasan a ser 0.0001, 1.0001, 2.0001...,
         * así que un `1` normal queda `:invalid` ("los dos valores válidos más aproximados
         * son 0.0001 y 1.0001") y la flecha de subir salta a 2.0001. Medido en Chrome 152.
         * Con `step="any"` la flecha avanza de 1 en 1 y se detiene al llegar a la cota:
         * desde 5 con min=0.0001 baja 4,3,2,1 y ahí se queda; con min=0 llega a 0.
         */
        step={acotado ? 'any' : undefined}
        value={value == null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
        /*
         * El atributo `min` solo frena los botoncitos del control; TECLEAR un valor por
         * debajo sigue permitido (el input queda `:invalid`, pero el valor llega al
         * formulario igual). El clamp al perder el foco es lo que cierra esa vía.
         *
         * Efecto sobre el mensaje de `validations`: en un numérico acotado el clamp
         * corre ANTES de que el mensaje de `min`/`max` pueda enseñarse, así que el
         * usuario ve el valor ya corregido y ningún texto en rojo. Es deliberado —
         * corregirlo es mejor que decirle que está mal y dejárselo puesto—; el mensaje
         * de esas dos reglas queda como respaldo para donde el clamp no aplica.
         */
        onBlur={(e) => {
          const raw = e.target.value;
          if (raw === '') return;
          const n = Number(raw);
          if (!Number.isFinite(n)) return;
          if (min !== undefined && n < min) onChange(min);
          else if (max !== undefined && n > max) onChange(max);
        }}
        placeholder={placeholder}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-secondary dark:text-dark-content-secondary pointer-events-none select-none">
          {suffix}
        </span>
      )}
    </div>
  );
}

function DateInput({
  value,
  onChange,
  hasError,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  hasError: boolean;
}) {
  return (
    <input
      type="date"
      className={clsx(inputBase, controlHeight, hasError && inputError, 'tabular-nums')}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
  placeholder,
  hasError,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  options?: Array<{ label: string; value: string | number }>;
  placeholder?: string;
  hasError: boolean;
}) {
  return (
    <select
      className={clsx(inputBase, controlHeight, hasError && inputError, 'cursor-pointer')}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// ─── SearcherInput — LookupField real o fallback ──────────

/**
 * Control de searcher (campo foráneo): renderiza el `LookupField` real cuando hay
 * `lookupConfig` + `fetcher`, y degrada a un input de texto con lupa cuando no.
 *
 * Se exporta porque el listado lo reutiliza para los filtros de columna foránea
 * (`SearchField.type: 'lookup'`): filtrar por una FK debe ofrecer el mismo
 * buscador que el campo del formulario, no un input donde escribir el id a mano.
 */
export function SearcherControl({
  value,
  onChange,
  placeholder,
  hasError,
  lookupConfig,
  fetcher,
  onRecordSelect,
  getLookupFilters,
  lookupDependencies,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
  hasError: boolean;
  lookupConfig?: LookupFieldConfig;
  fetcher?: unknown;
  onRecordSelect?: (record: Record<string, unknown> | null) => void;
  getLookupFilters?: () => unknown;
  lookupDependencies?: unknown[];
  disabled?: boolean;
}) {
  const { t } = useMasterDocumentTranslation();
  // Si hay lookupConfig y fetcher, renderizar LookupField real
  if (lookupConfig && fetcher) {
    // Campos de ETIQUETA del dropdown: se derivan del displayTemplate cuando
    // existe (p.ej. "{code} - {name}" → ["code", "name"]); si no hay template
    // se mantienen los displayFields configurados (sin regresión).
    const labelFields = lookupConfig.displayTemplate
      ? [...lookupConfig.displayTemplate.matchAll(/\{(\w+)\}/g)].map((m) => m[1])
      : lookupConfig.displayFields;
    // Campos que SÍ deben traerse del backend: los displayFields originales más
    // los campos de origen del bindFields (Record formulario→registro), para no
    // romper el autollenado en endpoints genéricos que solo devuelven lo pedido.
    // Se pasan como `bindFields` del LookupField (se piden pero NO se muestran).
    const fetchFields = [
      ...new Set([
        ...lookupConfig.displayFields,
        ...Object.values(lookupConfig.bindFields ?? {}),
      ]),
    ];
    return (
      <LookupField
        entity={lookupConfig.entity}
        displayFields={labelFields}
        bindFields={fetchFields}
        searchFields={lookupConfig.searchFields}
        displayTemplate={lookupConfig.displayTemplate}
        fetcher={fetcher as Fetcher}
        value={value ? String(value) : null}
        onChange={(record: Record<string, unknown> | null) => {
          if (!record) {
            onChange(null);
            onRecordSelect?.(null);
            return;
          }
          const valField = lookupConfig.valueField ?? 'Id';
          let newValue = record[valField];
          // Fallback Id → id
          if (newValue === undefined && valField === 'Id' && record['id'] !== undefined) {
            newValue = record['id'];
          }
          onChange(newValue ?? null);
          onRecordSelect?.(record);
        }}
        placeholder={placeholder ?? t('master_document.field.searchPlaceholder')}
        pageSize={lookupConfig.pageSize ?? 10}
        filters={lookupConfig.filters as never}
        getFilters={getLookupFilters as never}
        dependencies={lookupDependencies}
        disabled={disabled}
        error={hasError}
      />
    );
  }

  // Fallback: input de texto simple con icono de búsqueda
  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-secondary dark:text-dark-content-secondary pointer-events-none"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        className={clsx(inputBase, controlHeight, hasError && inputError, 'pl-9')}
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? t('master_document.field.searchPlaceholder')}
        aria-label={t('master_document.field.search')}
        disabled={disabled}
      />
    </div>
  );
}

function TextareaInput({
  value,
  onChange,
  placeholder,
  hasError,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
  hasError: boolean;
}) {
  return (
    <textarea
      className={clsx(inputBase, hasError && inputError, 'min-h-[72px] resize-y')}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
    />
  );
}

function ToggleInput({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const checked = Boolean(value);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={clsx(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-all duration-150',
        'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
        'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
        checked ? 'bg-primary-inverse-background dark:bg-dark-bg-inverse' : 'bg-border-primary dark:bg-dark-border-primary',
      )}
      onClick={() => onChange(!checked)}
    >
      <span
        className={clsx(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-bg-primary dark:bg-dark-content-primary shadow-sm transition-transform duration-150',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
}

function CheckboxInput({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const checked = Boolean(value);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      className={clsx(
        'flex h-[18px] w-[18px] items-center justify-center rounded-sm border-2 transition-all duration-150 cursor-pointer',
        'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
        'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
        checked
          ? 'border-primary-600 bg-primary-inverse-background dark:bg-dark-bg-inverse'
          : 'border-border-primary bg-bg-primary dark:border-dark-border-primary dark:bg-dark-bg-primary',
      )}
      onClick={() => onChange(!checked)}
    >
      {checked && (
        <svg className="h-3 w-3 text-primary-inverse-content dark:text-dark-content-inverse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

// ─── FieldRenderer ─────────────────────────────────────────

/**
 * Renderizador genérico de campos del documento maestro.
 *
 * Selecciona el control apropiado (text, number, currency, select, toggle, etc.)
 * según `fieldDef.control` y aplica el estado de solo lectura, error y accesibilidad.
 * Compatible con LookupField cuando se provee `lookupConfig` y `fetcher`.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - FieldRendererProps, FieldDescriptor, ControlType
 *
 * @example
 * ```tsx
 * <FieldRenderer
 *   fieldDef={{ key: 'cliente', label: 'Cliente', control: 'searcher', order: 1, span: 1, editableOn: ['DRAFT'] }}
 *   value="CLI-001"
 *   onChange={(v) => updateField('cliente', v)}
 *   editable={true}
 * />
 * ```
 */
export function FieldRenderer({
  fieldDef,
  value,
  onChange,
  editable,
  error,
  readOnly,
  className,
  lookupConfig,
  fetcher,
  onRecordSelect,
  getLookupFilters,
  lookupDependencies,
  disabled,
  locked,
  lockTooltip,
  lockTooltipKey,
  customValidators,
}: FieldRendererProps) {
  const { t } = useMasterDocumentTranslation();
  // Los mismos formateadores que la grilla de líneas: un importe se ve igual en
  // toda la pantalla, no según el sitio donde aparezca.
  const { formatCurrency, formatAmount } = useMasterDocumentFormatters();
  // El campo se considera "tocado" en cuanto el foco sale de su control. Antes de eso
  // no se le grita al usuario por un campo que aun no ha llegado a llenar.
  const [touched, setTouched] = useState(false);
  // Un campo bloqueado siempre se muestra como solo-lectura, independientemente
  // de su editabilidad por estado.
  const isReadOnly =
    locked ||
    !editable ||
    readOnly ||
    fieldDef.control === 'readonly' ||
    fieldDef.control === 'display';

  // Precedencia: clave i18n del template > texto literal del template > default
  // del kit. Antes `lockTooltipKey` estaba declarado y nunca se leia.
  const lockTooltipText = lockTooltipKey
    ? t(lockTooltipKey)
    : lockTooltip ?? t('master_document.field.lockTooltip');

  const isMono = ['number', 'currency', 'percentage', 'date'].includes(fieldDef.control);

  // Validación en cliente de `validations`. Se evalúa siempre (es barato y puro) pero
  // solo se ENSEÑA cuando el campo ya se tocó: así el formulario recién abierto no
  // aparece sembrado de mensajes en rojo.
  const clientError = useMemo(
    () =>
      isReadOnly || disabled
        ? null
        : evaluateFieldValidations(fieldDef, value, customValidators),
    [fieldDef, value, customValidators, isReadOnly, disabled],
  );

  /*
   * Precedencia: el error del SERVIDOR gana siempre. Es el autoritativo —llega tras
   * intentar guardar y puede decir cosas que el cliente no puede saber—, y taparlo con
   * un mensaje local sería mentirle al usuario sobre por qué no se guardó.
   */
  const shownError = error ?? (touched ? clientError : null);

  const hasError = !!shownError && shownError.severity === 'error';
  const hasWarning = !!shownError && shownError.severity === 'warning';
  const errorId = shownError ? `field-error-${fieldDef.key}` : undefined;

  // Un campo obligatorio editable y sin valor se resalta con borde rojo desde el
  // primer render, sin esperar a que se toque: el asterisco ya avisa de que hace
  // falta, y el borde solo lo hace visible. El MENSAJE sí espera al `touched`.
  const isRequiredEmpty =
    !!fieldDef.required &&
    !isReadOnly &&
    !disabled &&
    REQUIRED_HIGHLIGHT_CONTROLS.includes(fieldDef.control) &&
    isEmptyValue(value);

  // El borde rojo se prende tanto por un error explícito como por requerido-vacío.
  const showErrorBorder = hasError || isRequiredEmpty;

  // Render control
  function renderControl(control: ControlType) {
    const placeholder = fieldDef.placeholder;
    const options = fieldDef.options;
    // Cotas declaradas en el template (`validations`) llevadas al control numérico.
    // Hasta ahora `validations` no lo leía nadie en el kit, así que un `min` declarado
    // era configuración muerta y la flecha de bajar entraba en 0 y en negativo.
    const minNum = boundOf(fieldDef.validations, 'min');
    const maxNum = boundOf(fieldDef.validations, 'max');

    switch (control) {
      case 'text':
        return <TextInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} />;
      case 'number':
        // El formato manda sobre el control también EN EDICIÓN, no sólo en lectura
        // (§M.45). Un importe declarado `control: 'number'` con `format: 'currency'`
        // —que es como está media plantilla, porque `control` responde a «cómo se
        // captura»— se veía sin signo y sin separador mientras su propia columna lo
        // pintaba `$ 500.000,00`.
        if (fieldDef.format === 'currency') {
          return <CurrencyInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} prefijo="$" decimales={2} min={minNum} max={maxNum} />;
        }
        if (fieldDef.format === 'percentage') {
          return <CurrencyInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} sufijo="%" decimales={2} min={minNum} max={maxNum} />;
        }
        return <NumberInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} min={minNum} max={maxNum} />;
      case 'currency':
        return <CurrencyInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} prefijo="$" decimales={2} min={minNum} max={maxNum} />;
      case 'percentage':
        return <CurrencyInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} sufijo="%" decimales={2} min={minNum} max={maxNum} />;
      case 'date':
        return <DateInput value={value} onChange={onChange} hasError={showErrorBorder} />;
      case 'select':
        return <SelectInput value={value} onChange={onChange} options={options} placeholder={placeholder} hasError={showErrorBorder} />;
      case 'searcher':
      case 'autocomplete':
        return (
          <SearcherControl
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            hasError={showErrorBorder}
            lookupConfig={lookupConfig}
            fetcher={fetcher}
            onRecordSelect={onRecordSelect}
            getLookupFilters={getLookupFilters}
            lookupDependencies={lookupDependencies}
            disabled={disabled}
          />
        );
      case 'textarea':
        return <TextareaInput value={value} onChange={onChange} placeholder={placeholder} hasError={showErrorBorder} />;
      case 'toggle':
        return <ToggleInput value={value} onChange={onChange} />;
      case 'checkbox':
        return <CheckboxInput value={value} onChange={onChange} />;
      case 'readonly':
      case 'display':
        return <ReadonlyField value={value} />;
      default: {
        const _exhaustive: never = control;
        return <ReadonlyField value={_exhaustive} />;
      }
    }
  }

  // Render solo-lectura: para searcher/autocomplete resuelve el id a su etiqueta
  // ("código - nombre") reutilizando fetcher + lookupConfig; para select resuelve
  // el value crudo a la etiqueta de su opción (mismas options que en edición); el
  // resto de controles mantienen el render plano del valor crudo (sin regresión).
  function renderReadonly() {
    const control = fieldDef.control;
    /*
     * Un importe en SOLO LECTURA se formatea como importe. Antes se pintaba
     * `String(value)` —el número crudo, `500000`— mientras el mismo dato en la grilla
     * de líneas salía `$ 500.000,00`: el mismo valor con dos caras según dónde
     * apareciera. Se usa el MISMO formateador que la grilla, así que el signo, el
     * separador de miles y los dos decimales son los mismos en toda la pantalla.
     *
     * El control editable ya no lo necesita: lleva `prefix="$"`, que es lo que da a
     * entender que el campo es un valor mientras se teclea.
     */
    // El formato manda sobre el control, y es la REGLA GENERAL: `control` dice cómo
    // se captura el dato y `format` qué es. Mirando sólo el control, un importe
    // calculado por el servidor —`control: 'readonly'`, porque no se teclea— se
    // pintaba crudo (`500000`) mientras el mismo importe salía `$ 500.000,00` en la
    // grilla. Medido en dev el 2026-09-09 en la pestaña «Costo» del modal de línea:
    // seis campos de dinero sin separador, sin signo y sin decimales.
    const formato = fieldDef.format ?? (control === 'currency' || control === 'percentage' ? control : undefined);
    const esNumero = value != null && value !== '' && Number.isFinite(Number(value));
    if (formato === 'currency' && esNumero) {
      return <ReadonlyField value={formatCurrency(Number(value))} isMono />;
    }
    if (formato === 'percentage' && esNumero) {
      return <ReadonlyField value={`${formatAmount(Number(value))} %`} isMono />;
    }
    if (formato === 'number' && esNumero) {
      return <ReadonlyField value={formatAmount(Number(value))} isMono />;
    }
    if ((control === 'searcher' || control === 'autocomplete') && lookupConfig && fetcher) {
      return <LookupReadonlyValue id={value} lookupConfig={lookupConfig} fetcher={fetcher} isMono={isMono} />;
    }
    if (control === 'select') {
      // Buscar la opción cuyo value coincide con el valor del campo y mostrar su
      // label. Si las opciones no cargaron o no hay match, fallback al value crudo.
      const match = fieldDef.options?.find((opt) => String(opt.value) === String(value));
      return <ReadonlyField value={match ? match.label : value} isMono={isMono} />;
    }
    return <ReadonlyField value={value} isMono={isMono} />;
  }

  return (
    <div className={clsx('flex flex-col', className)}>
      {/* Label */}
      <label className={clsx(labelBase, 'flex items-center gap-1')}>
        <span>{fieldDef.label}</span>
        {fieldDef.required && !isReadOnly && (
          <span className="text-error-content ml-0.5">*</span>
        )}
        {locked && (
          <span
            className="inline-flex cursor-not-allowed text-content-tertiary dark:text-dark-content-tertiary"
            title={lockTooltipText}
            aria-label={lockTooltipText}
          >
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        )}
      </label>

      {/* Control */}
      {locked ? (
        <div className="cursor-not-allowed" title={lockTooltipText}>
          {renderReadonly()}
        </div>
      ) : isReadOnly ? (
        renderReadonly()
      ) : (
        <div
          className="relative"
          aria-invalid={showErrorBorder || undefined}
          aria-describedby={errorId}
          /*
           * `onBlur` en el contenedor y no en cada control: en React el evento burbujea
           * (usa la semántica de `focusout`), así que un solo manejador cubre todos los
           * tipos de control, incluidos los que el kit compone por dentro.
           */
          onBlur={() => setTouched(true)}
        >
          {renderControl(fieldDef.control)}
          {hasWarning && (
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600 dark:text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          )}
        </div>
      )}

      {/* Error / Warning message */}
      {/*
        Mensaje del canon (ADR-027 §D.6): icono + texto, `flex items-center gap-[3px] mt-1
        text-xs`, icono de 11x11 que NO es decoración — es lo que hace legible la línea como
        error sin depender sólo del color. Antes era texto suelto sin icono ni margen.
      */}
      {shownError && (
        <span
          id={errorId}
          role="alert"
          className={clsx(
            'flex items-center gap-[3px] mt-1 text-xs',
            shownError.severity === 'error' ? 'text-[#dc2626]' : 'text-amber-600 dark:text-amber-400',
          )}
        >
          <svg
            className="w-[11px] h-[11px] flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
              clipRule="evenodd"
            />
          </svg>
          {shownError.message}
        </span>
      )}
    </div>
  );
}
